/**
 * Multi-Airport Selector Component
 * VOY-101: Shows multiple airports for a region and lets user choose
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Plane, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Airport {
  code: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  airport_name: string;
  timezone?: string;
  distanceFromCenter: number;
}

interface MultiAirportSelectorProps {
  region: string;
  airports: Airport[];
  onSelect: (airportCode: string) => void;
  onSelectAll: () => void;
  onCancel: () => void;
  selectedCode?: string;
}

export function MultiAirportSelector({
  region,
  airports,
  onSelect,
  onSelectAll,
  onCancel,
  selectedCode
}: MultiAirportSelectorProps) {
  
  if (airports.length === 0) {
    return null;
  }

  return (
    <Card className="border-[#5B6FED] border-2 shadow-lg">
      <CardContent className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-[#5B6FED]" />
            <h3 className="font-semibold text-lg">
              Multiple airports found in {region}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {airports.length} airports available. Select one or search all for best prices.
          </p>
        </div>

        {/* Airport List */}
        <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto">
          {airports.slice(0, 10).map((airport) => (
            <button
              key={airport.code}
              onClick={() => onSelect(airport.code)}
              className={cn(
                'w-full p-3 rounded-lg border-2 text-left transition-all',
                selectedCode === airport.code
                  ? 'border-[#5B6FED] bg-[#E8E8FF]'
                  : 'border-gray-200 hover:border-[#5B6FED]/50 hover:bg-gray-50'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className={cn(
                    'w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0',
                    selectedCode === airport.code ? 'bg-[#5B6FED]' : 'bg-[#E8E8FF]'
                  )}>
                    <span className={cn(
                      'font-bold text-sm',
                      selectedCode === airport.code ? 'text-white' : 'text-[#5B6FED]'
                    )}>
                      {airport.code}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {airport.city}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {airport.airport_name || `${airport.code} Airport`}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {airport.distanceFromCenter.toFixed(0)} km from center
                    </div>
                  </div>
                </div>

                {selectedCode === airport.code && (
                  <Check className="w-5 h-5 text-[#5B6FED] flex-shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>

        {airports.length > 10 && (
          <p className="text-xs text-muted-foreground mb-4 text-center">
            Showing top 10 of {airports.length} airports
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1 rounded-full"
          >
            Cancel
          </Button>
          
          <Button
            onClick={onSelectAll}
            variant="outline"
            className="flex-1 rounded-full border-[#5B6FED] text-[#5B6FED] hover:bg-[#E8E8FF]"
          >
            <Plane className="w-4 h-4 mr-2" />
            Search All {airports.length}
          </Button>
          
          {selectedCode && (
            <Button
              onClick={() => onSelect(selectedCode)}
              className="flex-1 rounded-full bg-[#5B6FED] hover:bg-[#4A5FDD]"
            >
              Continue with {selectedCode}
            </Button>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-900">
            💡 <strong>Tip:</strong> "Search All" will compare prices across all {airports.length} airports 
            and show you the best deals from any airport in {region}.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

